/**
 * MRV 2026-08-19: vanilla-JS controller for the Liquid cart drawer
 * (sections/cart-drawer.liquid + snippets/cart-drawer-*.liquid).
 *
 * Replaces the Vue-driven minicart that used to live in
 * snippets/header__minicart.liquid inside the header's #app Vue instance.
 * Scope is the cart drawer ONLY: header, search, megamenu, wishlist header
 * icon, PDP, and every other Vue-driven feature are untouched and keep
 * working exactly as before.
 *
 * Every algorithm below (free-shipping threshold + geolocation, Click &
 * Collect / delivery stock lookups against Retail Express, out-of-stock
 * classification) is ported line-for-line from the compiled Vue app in
 * assets/theme.js, so the drawer's behaviour matches the old one exactly.
 * Search that file for the quoted method names in the comments if you want
 * to diff this against the original.
 *
 * Debugging the delivery/Click & Collect out-of-stock pipeline: run
 * `window.__mrvCartDrawerDebug = true` in the console BEFORE opening the
 * drawer (or before changing a quantity), then watch the console — it logs
 * the raw Retail Express response, the computed stock map, and the exact
 * per-item OOS decision at each step. Paste that output back if the OOS
 * banners still don't behave as expected; it'll show exactly which step
 * disagrees with what Retail Express actually returned.
 */
(function () {
  'use strict';
  if (window.__mrvCartDrawer) return;
  window.__mrvCartDrawer = true;

  var ROOT_ID = 'cart-drawer';
  var SECTION_ID = 'cart-drawer';

  // Same Google Maps key already loaded (conditionally) in layout/theme.liquid
  // for product/cart pages. The drawer can be opened from ANY page, so it
  // lazily loads the API itself the first time it's needed — this mirrors
  // the old header Vue component's `ensureGoogleMapsLoaded()` call in its
  // `isMinicartOpen` watcher.
  var GOOGLE_MAPS_KEY = 'AIzaSyCBdtRK1xXV1BS73MdL7Pdv6nwhE7EVGYY';

  // Retail Express outlet/stock API. Ported verbatim from assets/theme.js
  // (webpack module 3659, export `l4`). This resolves differently per
  // myshopify domain in the source theme; the mapping is reproduced as-is —
  // update it here if you add new environments.
  var RE_ENDPOINTS = {
    'mr-vitamins-development-site.myshopify.com': 'https://shopify.retailexpress.com.au/api/shopify_stores/mr-vitamins-development-site/outlets',
    'mrvitamins-sandbox-store.myshopify.com': 'https://shopify.retailexpress.com.au/api/shopify_stores/mrvitamins-sandbox-store/outlets'
  };
  var RE_DEFAULT = RE_ENDPOINTS['mr-vitamins-development-site.myshopify.com'];
  function retailExpressUrl() {
    var host = (window.Shopify && Shopify.shop) || location.hostname;
    return RE_ENDPOINTS[host] || RE_DEFAULT;
  }

  // Google Place IDs used to test "is this lat/lng inside this region".
  // Ported verbatim from assets/theme.js (setFreeShippingValue).
  //
  // NOTE — pre-existing quirk carried over on purpose for behavioural parity:
  // the original code fetches only 3 metro place IDs (Sydney/Melbourne/
  // Brisbane) plus 5 state place IDs (ACT/SA/WA/TAS/NT), and reuses the
  // METRO bounds for the "inNSW"/"inVIC"/"inQLD" checks instead of fetching
  // separate NSW/VIC/QLD state boundaries. Practically this means
  // nsw_shipping_threshold / vic_shipping_threshold / qld_shipping_threshold
  // are dead settings today — whenever someone is inside those bounds,
  // special_shipping_threshold wins first. We've reproduced this exactly so
  // behaviour doesn't change as part of this migration; flagging it in case
  // you want a follow-up fix.
  // Ported verbatim from the live minicart's rendered DOM (not the dev
  // theme.js bundle, which disagreed with what's actually in production —
  // see cncStoreRowHtml()/renderCncStoreShell() below for why). Same tick/
  // cross path data as snippets/cart-drawer-tab-icon.liquid, but solid
  // colors here (#38BB0A green / #FF1A18 red) rather than the pale tab cross.
  var CNC_TICK_ICON_HTML = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M6 9.5L8.25 12L12.5 6.5M17.5 9.25C17.5 13.8063 13.8063 17.5 9.25 17.5C4.69365 17.5 1 13.8063 1 9.25C1 4.69365 4.69365 1 9.25 1C13.8063 1 17.5 4.69365 17.5 9.25Z" stroke="#38BB0A" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var CNC_CROSS_ICON_HTML = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M12.5 6L9.25 9.25M9.25 9.25L6 12.5M9.25 9.25L6 6M9.25 9.25L12.5 12.5M17.5 9.25C17.5 13.8063 13.8063 17.5 9.25 17.5C4.69365 17.5 1 13.8063 1 9.25C1 4.69365 4.69365 1 9.25 1C13.8063 1 17.5 4.69365 17.5 9.25Z" stroke="#FF1A18" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var PLACE_IDS = {
    sydneyMetro: 'ChIJP3Sa8ziYEmsRUKgyFmh9AQM',
    melbourneMetro: 'ChIJ90260rVG1moRkM2MIXVWBAQ',
    brisbaneMetro: 'ChIJM9KTrJpXkWsRQK_e81qjAgQ',
    act: 'ChIJuzQLsqNMFmsRcFlpp27qAAQ',
    sa: 'ChIJP7Mmxcc1t2oRQMaOYlQ2AwQ',
    wa: 'ChIJPXNH22yWMioR0FXfNbXwBAM',
    tas: 'ChIJp8-59JMKbqoREOfd0E3JAwQ',
    nt: 'ChIJPwRZn_ygwCwRIHwkKqgXAgM'
  };

  var root = document.getElementById(ROOT_ID);
  if (!root) return; // section not present on this page render for some reason

  var settingsEl = root.querySelector('[data-cart-drawer-settings]');
  var settings = {};
  try { settings = JSON.parse(settingsEl.textContent); } catch (e) { settings = {}; }

  var panel = root.querySelector('[data-cart-drawer-panel]');
  var mask = root.querySelector('.cart-drawer__mask');

  // MRV 2026-08-19 (diagnostic): watches for panel.innerHTML being replaced
  // MORE than once per action. Added because the Delivery tab's tick/cross
  // was observed to be momentarily correct right after checkDelivery()
  // resolved, then silently revert to the untouched Liquid default shortly
  // after — which only happens if something replaces this panel's content
  // AGAIN afterward without re-running the OOS check that follows it. Gated
  // on the debug flag being true AT LOG TIME (not setup time), since the
  // flag is normally flipped on in the console after this script has
  // already booted. Logs every replacement plus a stack trace, so the
  // culprit shows up automatically — no separate console snippet needed.
  new MutationObserver(function () {
    if (!window.__mrvCartDrawerDebug) return;
    console.log('[cart-drawer] panel content REPLACED at', new Date().toLocaleTimeString());
    console.trace('[cart-drawer] panel replace stack');
  }).observe(panel, { childList: true });

  var state = {
    isOpen: false,
    deliveryMethod: 'home',
    freeShippingValue: settings.generalThreshold || 150,
    postcode: '',
    outlets: [],
    selectedCNC: null,
    outOfStockCNC: [],
    outOfStockDelivery: [],
    deliveryStock: null,
    cartProductIDs: [], // [{id, quantity}] — variant ids, GIFT CARD lines excluded
    progressBarWidth: null, // last painted free-shipping bar width (%) — survives panel re-renders; see updateProgressBarFromDom()
    progressBarColor: null  // last painted free-shipping bar color — same reason
  };

  // ---------------------------------------------------------------------
  // Small utilities
  // ---------------------------------------------------------------------
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function getOutletNamesCnc() {
    try { return JSON.parse(sessionStorage.getItem('outletNamesCnc')) || []; } catch (e) { return []; }
  }
  function getDeliveryStores() {
    try { return JSON.parse(sessionStorage.getItem('deliveryStores')) || []; } catch (e) { return []; }
  }

  // ---------------------------------------------------------------------
  // Retail Express outlet-stock helpers.
  // Ported verbatim from assets/theme.js (webpack module 6165: qN, NT, Ix, EC, vA).
  // ---------------------------------------------------------------------

  // qN — filter+rename outlets to the CNC display list (settings.stores_list_cnc).
  function filterAndRenameOutlets(outlets, configList) {
    if (!configList || !configList.length) return outlets;
    var names = configList.map(function (c) { return c.name; });
    return outlets.filter(function (o) { return names.indexOf(o.name) > -1; }).map(function (o) {
      var match = configList.filter(function (c) { return c.name === o.name; })[0];
      var copy = Object.assign({}, o);
      copy.name = match ? match.display : o.name.replace(/[0-9]/g, '');
      return copy;
    });
  }

  // NT — filter outlets down to the delivery-stock source list (settings.stores_list_delivery).
  function filterOutlets(outlets, configList) {
    if (!configList || !configList.length) return outlets;
    var names = configList.map(function (c) { return c.name; });
    return outlets.filter(function (o) { return names.indexOf(o.name) > -1; });
  }

  // Ix — sum stock across multiple outlets into one combined { [variantId]: qty } map.
  function sumStock(outlets) {
    if (!outlets || !outlets.length) return {};
    var combined = {};
    outlets.forEach(function (outlet, idx) {
      Object.keys(outlet.stock || {}).forEach(function (id) {
        var qty = outlet.stock[id];
        var clamped = qty < 0 ? 0 : qty;
        combined[id] = idx === 0 ? clamped : (combined[id] !== undefined ? combined[id] + clamped : clamped);
      });
    });
    return combined;
  }

  // EC — build the combined "Delivery" pseudo-outlet, status is binary
  // (available only if every cart line has enough combined stock).
  function buildDeliveryStock(combinedStock, cartProductIDs) {
    var result = { external_id: 2, name: 'Delivery', stock: combinedStock };
    var anyShort = cartProductIDs.some(function (line) {
      var have = combinedStock[line.id];
      return have === undefined || have < line.quantity;
    });
    result.status = anyShort ? 'unavailable' : 'available';
    return result;
  }

  // vA — per-outlet CNC availability: available / partial / unavailable,
  // based on how many distinct cart lines that outlet can fully cover.
  function computeCncOutletStatuses(outlets, configList, cartProductIDs, totalLineCount) {
    return filterAndRenameOutlets(outlets, configList).map(function (outlet) {
      var covered = 0;
      Object.keys(outlet.stock || {}).forEach(function (id) {
        var have = outlet.stock[id];
        var line = cartProductIDs.filter(function (l) { return l.id === parseInt(id, 10); })[0];
        if (line && have >= line.quantity) covered += 1;
      });
      var status, label;
      if (covered === totalLineCount) { status = 'available'; label = 'Available'; }
      else if (covered > 0 && covered < totalLineCount) { status = 'partial'; label = covered + ' of ' + totalLineCount + ' Available'; }
      else { status = 'unavailable'; label = 'Unavailable'; }
      var copy = Object.assign({}, outlet);
      copy.status = status;
      copy.statusLabel = label;
      return copy;
    });
  }

  // ---------------------------------------------------------------------
  // Google Maps lazy loader (mirrors ensureGoogleMapsLoaded() in theme.js)
  // ---------------------------------------------------------------------
  var googleMapsPromise = null;
  // IMPORTANT: this used to resolve/reject purely on the <script> tag's own
  // load/error events. That's blind to the single most common real-world
  // failure mode for the Google Maps JS API: the SCRIPT FILE loads fine
  // (200 OK, fires "load") but the API itself then refuses to initialize
  // because of an invalid/restricted key (wrong domain, referrer
  // restriction, disabled billing, etc.) — Google logs that as its own
  // console error, but it does NOT fire the <script> tag's error event, so
  // the promise below would previously just... never settle. Every caller
  // (handleClickAndCollect(), checkDelivery() indirectly via this) would
  // then hang forever with no visible failure at all — exactly matching
  // "click and collect pincode response not popped up on page" with no
  // console output. Fixed with an explicit timeout + a check that
  // window.google.maps actually exists after "load" fires, both logged
  // loudly (not gated behind the debug flag) so a silent hang is now
  // impossible to miss.
  function ensureGoogleMapsLoaded() {
    if (window.google && window.google.maps) return Promise.resolve();
    if (googleMapsPromise) return googleMapsPromise;
    googleMapsPromise = new Promise(function (resolve, reject) {
      var settled = false;
      var timeoutId = setTimeout(function () {
        if (settled) return;
        settled = true;
        console.error('[cart-drawer] Google Maps API did not finish loading within 8s. Check the Network tab for a request to maps.googleapis.com/maps/api/js — if it never appears, or appears but the page console shows a Google error (RefererNotAllowedMapError / InvalidKeyMapError / ApiNotActivatedMapError), the API key this drawer uses is invalid or restricted for this domain.');
        reject(new Error('cart-drawer: Google Maps load timeout'));
      }, 8000);
      function onLoad() {
        if (settled) return;
        if (window.google && window.google.maps) {
          settled = true;
          clearTimeout(timeoutId);
          resolve();
        } else {
          // Script loaded, but the API didn't attach itself to window.google.maps —
          // this is the "loaded but refused to initialize" case described above.
          settled = true;
          clearTimeout(timeoutId);
          console.error('[cart-drawer] Google Maps script tag loaded, but window.google.maps is still undefined. This almost always means the API key is invalid or restricted for this domain — look for a red Google-logged error in the console (e.g. RefererNotAllowedMapError).');
          reject(new Error('cart-drawer: google.maps missing after script load'));
        }
      }
      function onError(e) {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        console.error('[cart-drawer] Google Maps script tag failed to load (network error / blocked request).', e);
        reject(e);
      }
      var existing = document.querySelector('script[data-cart-drawer-google-maps]');
      if (existing) {
        existing.addEventListener('load', onLoad);
        existing.addEventListener('error', onError);
        return;
      }
      var script = document.createElement('script');
      script.src = 'https://maps.googleapis.com/maps/api/js?key=' + GOOGLE_MAPS_KEY + '&libraries=places';
      script.async = true;
      script.setAttribute('data-cart-drawer-google-maps', 'true');
      script.addEventListener('load', onLoad);
      script.addEventListener('error', onError);
      document.head.appendChild(script);
    });
    return googleMapsPromise;
  }

  // ---------------------------------------------------------------------
  // Section fetch + swap (Shopify Ajax Cart "sections" API)
  // ---------------------------------------------------------------------
  function extractPanelHtml(sectionHtml) {
    var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    var freshPanel = doc.querySelector('[data-cart-drawer-panel]');
    return freshPanel ? freshPanel.innerHTML : null;
  }

  // MRV 2026-08-19 (bug fix): data-cart-total-price lives on the OUTER
  // #cart-drawer element (see sections/cart-drawer.liquid), which is
  // OUTSIDE [data-cart-drawer-panel] — the only part applyPanelHtml() ever
  // replaces. That meant this attribute never changed after page load, no
  // matter how many times the cart itself changed, so
  // updateProgressBarFromDom() kept computing the free-shipping bar's width
  // (and, after the earlier color fix, its color too) off the ORIGINAL
  // page-load total forever — the bar visibly "stopped moving" the moment
  // anything mutated the cart. Pull the fresh total out of the same
  // section HTML response and keep the live root's attribute in sync.
  function extractCartTotalPrice(sectionHtml) {
    var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    var freshRoot = doc.querySelector('[data-cart-drawer]');
    return freshRoot ? freshRoot.getAttribute('data-cart-total-price') : null;
  }

  function applyPanelHtml(html, sectionHtml) {
    if (html === null) return;
    if (sectionHtml) {
      var freshTotal = extractCartTotalPrice(sectionHtml);
      if (freshTotal !== null) root.setAttribute('data-cart-total-price', freshTotal);
    }
    panel.innerHTML = html;
    // Re-apply in-memory UI state that lives in the swapped-out DOM
    // (Liquid always re-renders the "home" tab as default).
    setActiveTab(state.deliveryMethod, { skipRefetch: true });
    syncWishlistIcons();
    if (state.deliveryMethod === 'home') renderDeliveryOOS(); else renderCncOOS();
    updateProgressBarFromDom();
    updateHeaderBadge();
    // MRV 2026-08-19 (bug fix): this function runs on EVERY panel refresh —
    // including the most common one, adding a brand new item to the cart
    // from a product page (see the fetch/XHR interception above that calls
    // refreshSection().then(openDrawer)). Until now nothing here re-ran
    // checkDelivery()/handleClickAndCollect() — only a manual quantity +/-
    // did that (via refreshDeliveryAndCncChecks() in changeLineQuantity()).
    // That meant state.outOfStockDelivery / state.outOfStockCNC were
    // computed ONCE at page load and then went stale: add a fresh CNC-only
    // or out-of-stock item to the cart, and the Delivery tab kept showing
    // whatever tick/cross state existed before that item was even in the
    // cart — the newly-added line was never evaluated at all. This is the
    // real cause of "message appears now, but delivery icon still shows a
    // green tick" — the icon color fix in cart-drawer-tab-icon.liquid was
    // correct but irrelevant, because updateTabChecks() was running against
    // stale (often empty) out-of-stock arrays that predated the new line.
    // Always re-check delivery on refresh (matches the original's own
    // behaviour of running this on every `cart` watcher tick, regardless of
    // which tab is active or whether Click & Collect is enabled — see the
    // boot call at the bottom of this file), and re-run the Click & Collect
    // lookup too if a store was already selected, so both tabs reflect the
    // CURRENT cart contents immediately, not just after a quantity nudge.
    checkDelivery();
    if (state.selectedCNC) handleClickAndCollect();
  }

  // OPTIONAL — only does anything if you add id="header-cart-count" to the
  // <small> cart-count bubble in sections/header.liquid (see the delivery
  // notes). The header's own count badge is still bound to the untouched
  // Vue app's shared cart store, so it won't see mutations made from this
  // drawer until Vue itself happens to refetch the cart for some other
  // reason. This keeps it in sync directly, without touching Vue.
  function updateHeaderBadge() {
    var badge = document.getElementById('header-cart-count');
    if (!badge) return;
    var count = qsa('[data-cart-drawer-qty-input]', panel).reduce(function (sum, el) {
      return sum + (parseInt(el.value, 10) || 0);
    }, 0);
    badge.textContent = count;
    badge.hidden = count === 0;
  }

  function cartFetch(url, options) {
    options = options || {};
    options.headers = Object.assign({ 'Content-Type': 'application/json', Accept: 'application/json' }, options.headers || {});
    return fetch(url, options).then(function (r) {
      if (!r.ok) return r.json().then(function (body) { throw body; });
      return r.json();
    });
  }

  function refreshSection() {
    // Section Rendering API works against the CURRENT url (any template) —
    // sections/cart-drawer.liquid doesn't need to be included in that page's
    // own template, it just needs to exist under sections/. Using the
    // current path (not a hardcoded '/') keeps this safe on every page.
    var sep = window.location.search ? '&' : '?';
    var url = window.location.pathname + window.location.search + sep + 'sections=' + SECTION_ID;
    return fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) { applyPanelHtml(extractPanelHtml(data[SECTION_ID]), data[SECTION_ID]); });
  }

  function mutateCart(url, body) {
    body = Object.assign({}, body, { sections: SECTION_ID });
    return cartFetch(url, { method: 'POST', body: JSON.stringify(body) }).then(function (data) {
      var sectionHtml = data.sections && data.sections[SECTION_ID];
      if (sectionHtml) applyPanelHtml(extractPanelHtml(sectionHtml), sectionHtml);
      return data;
    });
  }

  // ---------------------------------------------------------------------
  // Open / close
  // ---------------------------------------------------------------------
  function openDrawer() {
    // Dropped the old `if (state.isOpen) return;` early-return guard. It's
    // called via `refreshSection().then(openDrawer)` from every add-to-cart
    // (product forms, quick-add, this drawer's own mutations) as well as
    // the cart icon click — if `state.isOpen` was ever true in JS memory
    // while the "is-open" class had, for any reason, not actually made it
    // onto the element (or been removed by something else), every
    // subsequent add-to-cart would keep refreshing the panel's CONTENT
    // (refreshSection() doesn't check state.isOpen) while silently skipping
    // re-adding the class that makes the drawer visible — content updates,
    // drawer never (re)appears. classList.add() is a harmless no-op when
    // the class is already present, so removing the guard costs nothing
    // and closes off that entire class of desync.
    state.isOpen = true;
    root.classList.add('is-open');
    document.documentElement.classList.add('noscroll');
    syncWishlistIcons();
  }
  function closeDrawer() {
    if (!state.isOpen) return;
    state.isOpen = false;
    root.classList.remove('is-open');
    document.documentElement.classList.remove('noscroll');
  }
  function toggleDrawer() { state.isOpen ? closeDrawer() : openDrawer(); }

  qsa('.js-cart-drawer-toggle').forEach(function (el) {
    el.addEventListener('click', function (e) { e.preventDefault(); refreshSection().then(openDrawer); });
  });
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-drawer-close]')) { e.preventDefault(); closeDrawer(); }
  });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeDrawer(); });

  // ---------------------------------------------------------------------
  // Bridge: open + refresh the drawer whenever ANYTHING on the site adds to
  // cart, including the existing Vue product forms / quick-add, which this
  // migration does not touch. Modelled on the same fetch/XHR interception
  // pattern already used by snippets/mrv-cart-dedupe.liquid and
  // snippets/mrv-cart-discount.liquid.
  // ---------------------------------------------------------------------
  function isAddToCart(url) { return /\/cart\/add(\.js)?(\?|$)/.test(String(url)); }

  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var method = (init && init.method) || (input && input.method) || 'GET';
    var p = origFetch.apply(this, arguments);
    if (String(method).toUpperCase() === 'POST' && isAddToCart(url)) {
      p.then(function (res) { if (res.ok) refreshSection().then(openDrawer); }).catch(function () {});
    }
    return p;
  };
  var origXhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__cartDrawerIsAdd = String(method).toUpperCase() === 'POST' && isAddToCart(url);
    return origXhrOpen.apply(this, arguments);
  };
  var origXhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__cartDrawerIsAdd) {
      this.addEventListener('loadend', function () {
        if (this.status >= 200 && this.status < 300) refreshSection().then(openDrawer);
      });
    }
    return origXhrSend.apply(this, arguments);
  };

  // ---------------------------------------------------------------------
  // Quantity change / remove / wishlist — delegated listeners on the
  // (stable) root element so they keep working after panel.innerHTML swaps.
  // ---------------------------------------------------------------------
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function changeLineQuantity(lineKey, quantity, itemEl) {
    var loading = itemEl && itemEl.querySelector('[data-cart-drawer-item-loading]');
    if (loading) loading.hidden = false;
    return mutateCart('/cart/change.js', { id: lineKey, quantity: quantity })
      .then(function () {
        refreshDeliveryAndCncChecks();
      })
      .catch(function (err) {
        console.error('cart-drawer: quantity update failed', err);
        refreshSection();
      });
  }

  var debouncedQtyChange = debounce(function (lineKey, quantity, itemEl) {
    changeLineQuantity(lineKey, quantity, itemEl);
  }, 400);

  root.addEventListener('click', function (e) {
    var itemEl = e.target.closest('[data-cart-drawer-item]');

    if (e.target.closest('[data-cart-drawer-remove]') && itemEl) {
      changeLineQuantity(itemEl.getAttribute('data-line-key'), 0, itemEl);
      return;
    }
    if (e.target.closest('[data-cart-drawer-qty-increase]') && itemEl) {
      var inputInc = itemEl.querySelector('[data-cart-drawer-qty-input]');
      inputInc.value = parseInt(inputInc.value || '0', 10) + 1;
      debouncedQtyChange(itemEl.getAttribute('data-line-key'), parseInt(inputInc.value, 10), itemEl);
      return;
    }
    if (e.target.closest('[data-cart-drawer-qty-decrease]') && itemEl) {
      var inputDec = itemEl.querySelector('[data-cart-drawer-qty-input]');
      inputDec.value = Math.max(0, parseInt(inputDec.value || '0', 10) - 1);
      debouncedQtyChange(itemEl.getAttribute('data-line-key'), parseInt(inputDec.value, 10), itemEl);
      return;
    }
    if (e.target.closest('[data-cart-drawer-wishlist-toggle]')) {
      handleWishlistClick(e.target.closest('[data-cart-drawer-wishlist-toggle]'));
      return;
    }
    if (e.target.closest('[data-cart-drawer-checkout]')) {
      submitCheckout();
      return;
    }
  });

  root.addEventListener('input', function (e) {
    if (e.target.matches('[data-cart-drawer-qty-input]')) {
      var itemEl = e.target.closest('[data-cart-drawer-item]');
      var qty = Math.max(0, parseInt(e.target.value || '0', 10));
      debouncedQtyChange(itemEl.getAttribute('data-line-key'), qty, itemEl);
    }
  });

  // ---------------------------------------------------------------------
  // Wishlist toggle — reuses the SAME global iwish_addCollection /
  // iwish_remove / isInWishlist functions the rest of the theme already
  // uses (from the wishlist app's own script, not Vue-specific).
  // Ported from `handleWishlistClick` in assets/theme.js.
  // ---------------------------------------------------------------------
  function handleWishlistClick(toggleEl) {
    var dataEl = toggleEl.querySelector('[data-product]');
    var productId = toggleEl.getAttribute('data-product-id');
    var variantId = toggleEl.getAttribute('data-variant-id');
    var isOn = !toggleEl.querySelector('.wishlist-icon-on').hidden;
    try {
      if (isOn) {
        if (typeof iwish_remove === 'function') iwish_remove(window.jQuery ? jQuery(dataEl) : dataEl, String(variantId));
      } else {
        if (typeof iwish_addCollection === 'function') iwish_addCollection(window.jQuery ? jQuery(dataEl) : dataEl, String(variantId));
      }
    } catch (err) { console.error('cart-drawer: wishlist toggle failed', err); }
    setWishlistIconState(toggleEl, !isOn);
  }
  function setWishlistIconState(toggleEl, isOn) {
    toggleEl.querySelector('.wishlist-icon-off').hidden = isOn;
    toggleEl.querySelector('.wishlist-icon-on').hidden = !isOn;
  }
  function syncWishlistIcons() {
    if (typeof isInWishlist !== 'function') return;
    qsa('[data-cart-drawer-wishlist-toggle]', panel).forEach(function (toggleEl) {
      var productId = toggleEl.getAttribute('data-product-id');
      var variantId = toggleEl.getAttribute('data-variant-id');
      var on = false;
      try { on = isInWishlist(String(productId), String(variantId)); } catch (e) {}
      setWishlistIconState(toggleEl, !!on);
    });
  }

  // ---------------------------------------------------------------------
  // Free shipping progress bar + geolocation threshold.
  // Ported from setProgressBar() / setFreeShippingValue() in assets/theme.js.
  // ---------------------------------------------------------------------
  function updateProgressBarFromDom() {
    var bar = panel.querySelector('[data-cart-drawer-progress-bar]');
    if (!bar) return;
    var totalCents = parseFloat(root.getAttribute('data-cart-total-price')) || 0;
    var total = totalCents / 100;
    var unlocked = total >= state.freeShippingValue;
    var remaining = Math.max(0, state.freeShippingValue - total);
    var lockedLabel = bar.querySelector('[data-cart-drawer-progress-label]');
    var unlockedLabel = bar.querySelector('[data-cart-drawer-progress-unlocked-label]');
    var fill = bar.querySelector('[data-cart-drawer-progress-fill]');
    if (lockedLabel) {
      lockedLabel.hidden = unlocked;
      var amountEl = lockedLabel.querySelector('span');
      if (amountEl) amountEl.textContent = '$' + remaining.toFixed(2);
    }
    if (unlockedLabel) unlockedLabel.hidden = !unlocked;
    if (fill) {
      var targetWidth = unlocked ? 100 : Math.min(100, (total / state.freeShippingValue) * 100);
      // MRV 2026-08-19: the original renders two genuinely different-colored
      // bars here, not one — a real <vue-slider> styled orange (#feb700, via
      // theme.css ".progress-bar .vue-slider-process") while still saving up,
      // vs. a flat green (#ACD070) bar only once unlocked. Keep this in sync
      // with snippets/cart-drawer-progress-bar.liquid's initial render —
      // this call runs again once geolocation resolves the REAL threshold
      // (metro/state), which can flip `unlocked` after the page's first paint.
      var targetColor = unlocked ? '#ACD070' : '#feb700';
      // MRV 2026-08-19 (bug fix): adding the CSS transition on
      // [data-cart-drawer-progress-fill] alone didn't make the bar glide —
      // applyPanelHtml() re-renders the ENTIRE panel from fresh Liquid HTML
      // on every cart change, so `fill` here is a brand-new DOM node every
      // single time, not the same element from the previous render. A CSS
      // transition can't animate a node that mounts already AT its final
      // width; there's no "before" frame to interpolate from, so it just
      // snaps. Fake that "before" frame explicitly: paint the new node at
      // the PREVIOUS known width/color with the transition turned off,
      // force the browser to commit that frame (reading offsetWidth forces
      // a layout flush), then set the real target a tick later so the
      // transition in cart-drawer.css has something to actually animate
      // between. state.progressBarWidth/-Color persist across these
      // re-renders (they live on the JS `state` object, not the DOM), so
      // this correctly remembers the bar's position from before the swap.
      var prevWidth = state.progressBarWidth != null ? state.progressBarWidth : targetWidth;
      var prevColor = state.progressBarColor || targetColor;
      fill.style.transition = 'none';
      fill.style.width = prevWidth + '%';
      fill.style.backgroundColor = prevColor;
      void fill.offsetWidth; // force layout so the "before" frame above is committed
      fill.style.transition = '';
      requestAnimationFrame(function () {
        fill.style.width = targetWidth + '%';
        fill.style.backgroundColor = targetColor;
      });
      state.progressBarWidth = targetWidth;
      state.progressBarColor = targetColor;
    }
  }

  function setFreeShippingValueFromPosition(position) {
    var lat = parseFloat(position.coords.latitude);
    var lng = parseFloat(position.coords.longitude);
    if (isNaN(lat) || isNaN(lng)) { state.freeShippingValue = settings.generalThreshold; updateProgressBarFromDom(); return Promise.resolve(); }

    var cached = {
      sydneyMetro: sessionStorage.getItem('isSydneyMetro'),
      melbourneMetro: sessionStorage.getItem('isMelbourneMetro'),
      brisbaneMetro: sessionStorage.getItem('isBrisbaneMetro'),
      nsw: sessionStorage.getItem('inNSW'),
      vic: sessionStorage.getItem('inVIC'),
      qld: sessionStorage.getItem('inQLD'),
      act: sessionStorage.getItem('inACT'),
      sa: sessionStorage.getItem('inSA'),
      wa: sessionStorage.getItem('inWA'),
      tas: sessionStorage.getItem('inTAS'),
      nt: sessionStorage.getItem('inNT')
    };
    var haveCache = Object.keys(cached).some(function (k) { return cached[k] !== null; });

    function applyFlags(flags) {
      var f = flags;
      state.freeShippingValue =
        (f.sydneyMetro || f.melbourneMetro || f.brisbaneMetro) ? settings.specialThreshold :
        f.nsw ? (settings.nswThreshold || settings.generalThreshold) :
        f.vic ? (settings.vicThreshold || settings.generalThreshold) :
        f.qld ? (settings.qldThreshold || settings.generalThreshold) :
        f.act ? (settings.actThreshold || settings.generalThreshold) :
        f.sa ? (settings.saThreshold || settings.generalThreshold) :
        f.wa ? (settings.waThreshold || settings.generalThreshold) :
        f.tas ? (settings.tasThreshold || settings.generalThreshold) :
        (f.nt && settings.ntThreshold) || settings.generalThreshold;
      updateProgressBarFromDom();
    }

    if (haveCache) {
      applyFlags({
        sydneyMetro: JSON.parse(cached.sydneyMetro || 'false'),
        melbourneMetro: JSON.parse(cached.melbourneMetro || 'false'),
        brisbaneMetro: JSON.parse(cached.brisbaneMetro || 'false'),
        nsw: JSON.parse(cached.nsw || 'false'),
        vic: JSON.parse(cached.vic || 'false'),
        qld: JSON.parse(cached.qld || 'false'),
        act: JSON.parse(cached.act || 'false'),
        sa: JSON.parse(cached.sa || 'false'),
        wa: JSON.parse(cached.wa || 'false'),
        tas: JSON.parse(cached.tas || 'false'),
        nt: JSON.parse(cached.nt || 'false')
      });
      return Promise.resolve();
    }

    return ensureGoogleMapsLoaded().then(function () {
      var geocoder = new google.maps.Geocoder();
      function boundsFor(placeId) {
        return new Promise(function (resolve) {
          geocoder.geocode({ placeId: placeId }, function (results) { resolve(results[0].geometry.bounds); });
        });
      }
      return Promise.all([
        boundsFor(PLACE_IDS.sydneyMetro),
        boundsFor(PLACE_IDS.melbourneMetro),
        boundsFor(PLACE_IDS.brisbaneMetro),
        boundsFor(PLACE_IDS.act),
        boundsFor(PLACE_IDS.sa),
        boundsFor(PLACE_IDS.wa),
        boundsFor(PLACE_IDS.tas),
        boundsFor(PLACE_IDS.nt)
      ]).then(function (bounds) {
        var sydney = bounds[0], melbourne = bounds[1], brisbane = bounds[2],
          act = bounds[3], sa = bounds[4], wa = bounds[5], tas = bounds[6], nt = bounds[7];
        var point = new google.maps.LatLng(lat, lng);
        var flags = {
          sydneyMetro: sydney.contains(point),
          melbourneMetro: melbourne.contains(point),
          brisbaneMetro: brisbane.contains(point),
          // NOTE: reusing the metro bounds for state checks — see the
          // PLACE_IDS comment above for why this matches the original.
          nsw: sydney.contains(point),
          vic: melbourne.contains(point),
          qld: brisbane.contains(point),
          act: act.contains(point),
          sa: sa.contains(point),
          wa: wa.contains(point),
          tas: tas.contains(point),
          nt: nt.contains(point)
        };
        sessionStorage.setItem('isSydneyMetro', flags.sydneyMetro);
        sessionStorage.setItem('isMelbourneMetro', flags.melbourneMetro);
        sessionStorage.setItem('isBrisbaneMetro', flags.brisbaneMetro);
        sessionStorage.setItem('inNSW', flags.nsw);
        sessionStorage.setItem('inVIC', flags.vic);
        sessionStorage.setItem('inQLD', flags.qld);
        sessionStorage.setItem('inACT', flags.act);
        sessionStorage.setItem('inSA', flags.sa);
        sessionStorage.setItem('inWA', flags.wa);
        sessionStorage.setItem('inTAS', flags.tas);
        sessionStorage.setItem('inNT', flags.nt);
        applyFlags(flags);
      });
    }).catch(function (err) {
      console.error('cart-drawer: free shipping geolocation failed', err);
      state.freeShippingValue = settings.generalThreshold;
      updateProgressBarFromDom();
    });
  }

  function initFreeShipping() {
    state.freeShippingValue = settings.generalThreshold;
    updateProgressBarFromDom();
    if (settings.enableSpecialThreshold && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(setFreeShippingValueFromPosition, function () {
        // permission denied / unavailable — keep the general threshold.
      });
    }
  }

  // ---------------------------------------------------------------------
  // Delivery-method tabs, delivery stock check, and Click & Collect.
  // Ported from checkDelivery / handleClickAndCollect / selectCNC / resetCNC
  // / collateOutOfStockDelivery / collateOutOfStockCNC in assets/theme.js.
  // ---------------------------------------------------------------------
  function readCartProductIDsFromDom() {
    return qsa('[data-cart-drawer-item]', panel)
      .map(function (el) { return { id: parseInt(el.getAttribute('data-item-id'), 10), quantity: parseInt(el.querySelector('[data-cart-drawer-qty-input]').value, 10) }; })
      .filter(function (l) { return !isNaN(l.id) && l.quantity > 0; });
  }

  function buildVariantQtyQuery(cartProductIDs) {
    var ids = cartProductIDs.map(function (l) { return l.id; }).join(',');
    var qty = cartProductIDs.map(function (l) { return '&qty-' + l.id + '=' + l.quantity; }).join('');
    return 'variant_ids=' + ids + qty;
  }

  function checkDelivery() {
    var cartProductIDs = readCartProductIDsFromDom();
    state.cartProductIDs = cartProductIDs;
    if (!cartProductIDs.length) { state.deliveryStock = null; return Promise.resolve(); }
    var url = retailExpressUrl() + '?' + buildVariantQtyQuery(cartProductIDs);
    if (window.__mrvCartDrawerDebug) console.log('[cart-drawer] checkDelivery() fetching', url);
    return fetch(url).then(function (r) { return r.json(); }).then(function (data) {
      var deliveryConfig = getDeliveryStores();
      var filtered = filterOutlets(data.outlets || [], deliveryConfig);
      var combined = sumStock(filtered);
      if (window.__mrvCartDrawerDebug) {
        // Logged as a JSON string (not the live object) so it survives
        // copy/paste out of devtools intact — a collapsed object reference
        // like `{outlets: Array(7)}` loses everything once pasted as text.
        // Shows every outlet RE returned (name + this variant's stock at
        // each), which of those names actually matched the configured
        // "delivery stores" list, and the resulting combined total — so a
        // store-name mismatch (typo, different casing, etc.) between
        // Retail Express and the `stores_list_delivery` setting is visible
        // immediately instead of guessed at.
        console.log('[cart-drawer] RE outlets response (all)', JSON.stringify((data.outlets || []).map(function (o) {
          return { name: o.name, stock: o.stock };
        }), null, 2));
        console.log('[cart-drawer] configured delivery stores (from sessionStorage "deliveryStores")', JSON.stringify(deliveryConfig, null, 2));
        console.log('[cart-drawer] outlets matched as "delivery" after filterOutlets()', JSON.stringify(filtered.map(function (o) { return o.name; })));
        console.log('[cart-drawer] combined delivery stock map', JSON.stringify(combined, null, 2));
      }
      state.deliveryStock = buildDeliveryStock(combined, cartProductIDs);
      state.outOfStockDelivery = [];
      // Iterate every ACTUAL cart line (from the DOM), not just whatever
      // variant ids happen to be keys in the Retail Express stock response.
      // cncOnly is a cart LINE PROPERTY set at add-to-cart time — it has
      // nothing to do with what Retail Express reports, and must be
      // evaluated for every line even if RE returns no stock entry at all
      // for that variant (plausible for a genuinely non-stocked/CNC-only
      // SKU). The original iterates `Object.entries(deliveryStock.stock)`
      // instead, which only works if RE's response happens to include an
      // entry for every requested variant — decoupling this is strictly
      // safer and costs nothing when RE does return full coverage.
      cartProductIDs.forEach(function (line) {
        collateOutOfStockDelivery(state.deliveryStock, line.id);
      });
      if (window.__mrvCartDrawerDebug) console.log('[cart-drawer] outOfStockDelivery after check', state.outOfStockDelivery.slice());
      renderDeliveryOOS();
    }).catch(function (err) { console.error('cart-drawer: delivery stock check failed', err); });
  }

  function collateOutOfStockDelivery(deliveryStock, itemId) {
    // Normalized to a number regardless of what's passed in (checkDelivery()
    // now passes line.id, already numeric, but this stays defensive in case
    // anything else ever calls it with a string id) — see the fixed bug
    // below: renderDeliveryOOS() compares with `indexOf(parseInt(...))`, a
    // NUMBER, and "123" === 123 is false, so a string here would silently
    // never match and the banners would never show, no matter how correct
    // the out-of-stock detection itself was.
    var numericId = parseInt(itemId, 10);
    var itemEl = qs('[data-cart-drawer-item][data-item-id="' + numericId + '"]', panel);
    if (!itemEl) {
      if (window.__mrvCartDrawerDebug) console.log('[cart-drawer] collateOutOfStockDelivery: no DOM item for id', itemId);
      return;
    }
    var qty = parseInt(itemEl.querySelector('[data-cart-drawer-qty-input]').value, 10);
    var cncOnly = itemEl.getAttribute('data-cnc-only') === 'true';
    var stockForItem = deliveryStock.stock[numericId];
    var short = stockForItem === undefined || stockForItem < qty;
    if (cncOnly || short) {
      if (state.outOfStockDelivery.indexOf(numericId) === -1) state.outOfStockDelivery.push(numericId);
    } else {
      state.outOfStockDelivery = state.outOfStockDelivery.filter(function (id) { return id !== numericId; });
    }
  }

  function renderDeliveryOOS() {
    qsa('[data-cart-drawer-item]', panel).forEach(function (itemEl) {
      var itemId = itemEl.getAttribute('data-item-id');
      var cncOnly = itemEl.getAttribute('data-cnc-only') === 'true';
      var isOOS = state.outOfStockDelivery.indexOf(parseInt(itemId, 10)) > -1;
      var oosCncOnly = itemEl.querySelector('[data-cart-drawer-oos-cnc-only]');
      var oosGeneric = itemEl.querySelector('[data-cart-drawer-oos-generic]');
      oosCncOnly.hidden = !(isOOS && cncOnly);
      oosGeneric.hidden = !(isOOS && !cncOnly);
    });
    // Correction: the original has NO aggregate "Sorry, N of your items are
    // out of stock" summary for the Delivery tab — that summary banner only
    // exists for Click & Collect (`v-if="... && deliveryMethod === 'store'"`
    // in the old header__minicart.liquid). For Delivery, only the per-item
    // banners above exist. I'd previously added one anyway (rendered above
    // the item list, into a now-removed [data-cart-drawer-oos-delivery]
    // container) — that's what showed up floating above the item instead of
    // the real per-item message sitting under it. Removed.
    updateCheckoutState();
    updateTabChecks();
  }

  function collateOutOfStockCNC(store, itemId, qty) {
    var have = store ? store.stock[itemId] : undefined;
    var short = have !== undefined && have < qty;
    if (short) {
      if (state.outOfStockCNC.indexOf(itemId) === -1) state.outOfStockCNC.push(itemId);
    } else {
      state.outOfStockCNC = state.outOfStockCNC.filter(function (id) { return id !== itemId; });
    }
  }

  function renderCncOOS() {
    // Correction: the original has NO per-item OOS banner on the Click &
    // Collect tab at all — both `oos-cart-error` v-ifs in header__minicart's
    // item loop are gated on `deliveryMethod === 'home'` only. I'd been
    // showing the "unavailable for delivery" generic banner here too
    // whenever an item was in outOfStockCNC, which is wrong on two counts:
    // it's not in the original, and the text itself talks about delivery
    // while looking at the Click & Collect tab. CNC out-of-stock is
    // communicated ONLY via the aggregate banner below
    // (data-cart-drawer-oos-cnc / buildOosBanner()). Also make sure to
    // clear both per-item banners here so switching from the Delivery tab
    // (where the generic one may be showing) doesn't leave it stuck visible.
    qsa('[data-cart-drawer-item]', panel).forEach(function (itemEl) {
      var oosGeneric = itemEl.querySelector('[data-cart-drawer-oos-generic]');
      var oosCncOnly = itemEl.querySelector('[data-cart-drawer-oos-cnc-only]');
      oosCncOnly.hidden = true;
      oosGeneric.hidden = true;
    });
    var banner = panel.querySelector('[data-cart-drawer-oos-cnc]');
    if (banner) banner.innerHTML = state.outOfStockCNC.length ? buildOosBanner() : '';
    updateCheckoutState();
    updateTabChecks();
  }

  // Ported from the compiled <tabs> Vue component (assets/theme.js,
  // ~module 306167): each minicart tab shows a green tick or a cross next
  // to its label, driven by the same `check` array the original passed in:
  // [outOfStockDelivery.length <= 0, selectedCNC === null || outOfStockCNC.length <= 0].
  function updateTabChecks() {
    var tabs = panel.querySelector('[data-cart-drawer-tabs]');
    if (!tabs) return;
    var deliveryTabBtn = tabs.querySelector('[data-cart-drawer-tab="home"]');
    var deliveryOk = state.outOfStockDelivery.length <= 0;
    
    setTabIcon(deliveryTabBtn, deliveryOk);
    setTabIcon(tabs.querySelector('[data-cart-drawer-tab="store"]'), state.selectedCNC === null || state.outOfStockCNC.length <= 0);
  }
  function setTabIcon(tabBtn, ok) {
    if (!tabBtn) return;
    var check = tabBtn.querySelector('[data-cart-drawer-tab-check]');
    var cross = tabBtn.querySelector('[data-cart-drawer-tab-cross]');
    // MRV 2026-08-19 (bug fix): setting the `hidden` IDL property alone
    // relies on `#cart-drawer [hidden] { display: none !important; }` in
    // cart-drawer.css to actually win the cascade. That rule IS present and
    // the JS state HAS been proven correct via extensive console logging
    // (deliveryOk/isOOS computed right every time) — but the icon still
    // visibly stuck on the wrong one on the live theme, meaning SOMETHING
    // else in the site's combined CSS still beats it for these two specific
    // elements (an inherited rule from elsewhere in theme.css we haven't
    // isolated, load-order dependent, etc.). Rather than keep hunting for
    // it, force `display` directly via inline style with !important on
    // BOTH the shown and hidden icon every time — inline !important beats
    // any external/embedded stylesheet rule regardless of its own
    // specificity or !important, full stop. Deliberately setting an
    // explicit 'block'/'none' rather than clearing back to '' for the
    // "shown" icon too: falling back to the ambient cascade is exactly
    // what's been unreliable here, so this doesn't lean on it at all
    // either way. Still sets `.hidden` alongside it, for ARIA/semantics.
    if (check) {
      check.hidden = !ok;
      check.style.setProperty('display', ok ? 'block' : 'none', 'important');
    }
    if (cross) {
      cross.hidden = ok;
      cross.style.setProperty('display', ok ? 'none' : 'block', 'important');
    }
  }

  // Only ever called for Click & Collect (see renderDeliveryOOS() above,
  // which no longer has an aggregate banner at all for Delivery — see the
  // comment there). Ported to the ACTUAL theme.css classes for this —
  // .minicart__click-collect-oos-container / -oos-message / -oos-items —
  // rather than the ad-hoc border/padding layout this used to build, which
  // wasn't the real markup and didn't pick up the theme's own styling for
  // this banner (background, radius, spacing all come from theme.css).
  function buildOosBanner() {
    var list = state.outOfStockCNC;
    var items = list.map(function (id) { return qs('[data-cart-drawer-item][data-item-id="' + id + '"]', panel); }).filter(Boolean);
    var titles = items.map(function (el) {
      var titleEl = el.querySelector('.minicart_product-title-label');
      var url = el.querySelector('a[href]').getAttribute('href');
      return '<a href="' + url + '" class="text-red block text-xs underline">' + (titleEl ? titleEl.textContent : '') + '</a>';
    }).join('');
    var count = list.length;
    return (
      '<div class="minicart__click-collect-oos-container">' +
      '<div class="flex flex-col md:flex-row"><div class="flex">' +
      '<span class="w-[25px] h-[25px]">' + CNC_CROSS_ICON_HTML + '</span>' +
      '<div class="minicart__click-collect-oos-message">' +
      '<p>Sorry, [<span class="text-red font-bold">' + count + '</span>] of your items are out of stock.</p>' +
      '<div class="minicart__click-collect-oos-items">' + titles + '</div>' +
      '</div></div></div></div>'
    );
  }

  function setActiveTab(method, opts) {
    opts = opts || {};
    state.deliveryMethod = method;
    var tabs = panel.querySelector('[data-cart-drawer-tabs]');
    if (!tabs) return;
    tabs.setAttribute('data-delivery-method', method);
    qsa('[data-cart-drawer-tab]', tabs).forEach(function (btn) {
      var active = btn.getAttribute('data-cart-drawer-tab') === method;
      // Reuses the existing .tabs__tab--active styling from theme.css
      // (the same class the old <tabs>/<tab> Vue components applied).
      btn.classList.toggle('tabs__tab--active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    qsa('[data-cart-drawer-panel-for]', panel).forEach(function (p) {
      p.hidden = p.getAttribute('data-cart-drawer-panel-for') !== method;
    });
    if (method === 'home') renderDeliveryOOS(); else renderCncOOS();
    if (!opts.skipRefetch) {
      if (method === 'home' && !state.deliveryStock) checkDelivery();
      if (method === 'store' && state.selectedCNC) handleClickAndCollect();
    }
  }

  root.addEventListener('click', function (e) {
    var tabBtn = e.target.closest('[data-cart-drawer-tab]');
    
    if (tabBtn) {
      var tab = tabBtn.getAttribute('data-cart-drawer-tab');
      setActiveTab(tab);
      if (tab === 'store') {
        ensureGoogleMapsLoaded().catch(function (err) {
          console.error(
            'cart-drawer: Google Maps failed to load',
            err
          );
        });
      }
      return;
    }

    if (e.target.closest('[data-cart-drawer-cnc-check]')) { handleClickAndCollect(); return; }
    if (e.target.closest('[data-cart-drawer-cnc-reset]')) { resetCNC(); return; }
    var outletBtn = e.target.closest('[data-cart-drawer-outlet]');
    if (outletBtn) { selectCNC(outletBtn.getAttribute('data-external-id')); return; }
  });

  root.addEventListener('input', function (e) {
    if (e.target.matches('[data-cart-drawer-cnc-postcode]')) {
      state.postcode = e.target.value;
      var checkBtn = panel.querySelector('[data-cart-drawer-cnc-check]');
      if (checkBtn) checkBtn.disabled = !state.postcode;
    }
  });

  function resetCNC() {
    // Matches the original Vue resetCNC(): this.outlets = [], this.selectedCNC
    // = null, this.postcode = "" — nothing more. It doesn't hide the store
    // list wrapper itself; renderCncStoreShell() below hides it because
    // state.outlets is now empty (same `outlets.length > 1` gate as before).
    state.outlets = [];
    state.selectedCNC = null;
    state.postcode = '';
    state.outOfStockCNC = [];
    var form = panel.querySelector('[data-cart-drawer-cnc-form]');
    if (!form) return;
    var postcodeInput = form.querySelector('[data-cart-drawer-cnc-postcode]');
    if (postcodeInput) postcodeInput.value = '';
    var resetCheckBtn = form.querySelector('[data-cart-drawer-cnc-check]');
    if (resetCheckBtn) resetCheckBtn.disabled = true;
    renderCncStoreShell(form);
    renderCncOOS();
  }

  function handleClickAndCollect() {
    var form = panel.querySelector('[data-cart-drawer-cnc-form]');
    if (!form) return;
    var messageEl = form.querySelector('[data-cart-drawer-cnc-message]');
    messageEl.hidden = true;
    if (!state.postcode) {
      messageEl.textContent = 'Click & Collect is unavailable in your location.';
      messageEl.hidden = false;
      return Promise.resolve();
    }
    var cartProductIDs = readCartProductIDsFromDom();
    state.cartProductIDs = cartProductIDs;
    var lineCount = cartProductIDs.length;

    return ensureGoogleMapsLoaded().then(function () {
      var geocoder = new google.maps.Geocoder();
      return new Promise(function (resolve, reject) {
        geocoder.geocode({ address: 'NSW ' + state.postcode }, function (results, status) {
          if (status === google.maps.GeocoderStatus.OK) resolve(results[0].geometry.location);
          else reject(new Error('Postcode not found'));
        });
      });
    }).then(function (location) {
      var lat = location.lat(), lng = location.lng();
      var url = retailExpressUrl() + '?' + buildVariantQtyQuery(cartProductIDs) + '&latitude=' + lat + '&longitude=' + lng;
      return fetch(url).then(function (r) { return r.json(); });
    }).then(function (data) {
      var statuses = computeCncOutletStatuses(data.outlets || [], getOutletNamesCnc(), cartProductIDs, lineCount);
      state.outlets = statuses;
      if (!statuses.length) {
        messageEl.textContent = 'Click & Collect is unavailable in your location.';
        messageEl.hidden = false;
        renderCncStoreShell(form);
        return;
      }
      // NOT an auto-select-first-store: the live drawer's DOM shows the full
      // store list (nothing pre-picked) right after a postcode search — the
      // shopper has to click a row (selectCNC()) to choose one. We only
      // carry an EXISTING selection forward across a re-fetch (e.g. the
      // cart-quantity-changed watcher re-running this while a store is
      // already selected), matching the original Vue `cart` watcher's
      // `this.selectedCNC && this.handleClickAndCollect()` guard.
      var keepSelected = state.selectedCNC && statuses.filter(function (s) { return s.external_id === state.selectedCNC.external_id; })[0];
      state.selectedCNC = keepSelected || null;
      state.outOfStockCNC = [];
      if (state.selectedCNC) {
        cartProductIDs.forEach(function (line) { collateOutOfStockCNC(state.selectedCNC, line.id, line.quantity); });
      }
      renderCncStoreShell(form);
      renderCncOOS();
    }).catch(function (err) {
      console.error('cart-drawer: click & collect lookup failed', err);
      messageEl.textContent = err && err.message === 'Postcode not found' ? 'Postcode not found' : 'Click & Collect is unavailable in your location.';
      messageEl.hidden = false;
    });
  }

  function selectCNC(externalId) {
    var match = state.outlets.filter(function (o) { return String(o.external_id) === String(externalId); })[0];
    if (!match) return;
    state.selectedCNC = match;
    state.outOfStockCNC = [];
    state.cartProductIDs.forEach(function (line) { collateOutOfStockCNC(state.selectedCNC, line.id, line.quantity); });
    var form = panel.querySelector('[data-cart-drawer-cnc-form]');
    renderCncStoreShell(form);
    renderCncOOS();
  }

  // Ported to match the live drawer's actual DOM 1:1 (screenshots + the
  // rendered HTML the merchant sent over), which uses a plain tick/cross
  // status icon + text label — NOT the colored-pill "available"/"Mr
  // Vitamins" two-line design the /cart page's cart__delivery-method.liquid
  // uses. Those are two different components; this one only borrows the
  // minicart__cnc-* class names, which is what theme.css actually styles
  // for the drawer (.minicart__cnc-unavailable => grey text; there's no
  // colored-pill rule for the drawer at all).
  function cncStoreRowHtml(store, isSelected) {
    var icon = store.status === 'available' ? CNC_TICK_ICON_HTML : CNC_CROSS_ICON_HTML;
    var labelHtml = '<div class="flex flex-col minicart__cnc-store-label"><span>' + store.name + '</span></div>';
    if (isSelected) {
      // Matches the original's `v-if="selectedCNC"` row: icon + name +
      // an underlined "Change" link (click -> resetCNC()) instead of a
      // status label.
      return (
        '<div class="minicart__cnc-items flex cursor-pointer">' +
        '<div class="minicart__cnc-items-icon">' + icon + '</div>' +
        '<div class="flex minicart__cnc-item-label-container">' + labelHtml +
        '<span class="minicart__cnc-status-label underline" data-cart-drawer-cnc-reset>Change</span>' +
        '</div></div>'
      );
    }
    var statusClass = store.status === 'available' ? 'minicart__cnc-available'
      : store.status === 'unavailable' ? 'minicart__cnc-unavailable'
      : 'minicart__cnc-partial-unavailable';
    return (
      '<div class="minicart__cnc-items flex cursor-pointer" data-cart-drawer-outlet data-external-id="' + store.external_id + '">' +
      '<div class="minicart__cnc-items-icon">' + icon + '</div>' +
      '<div class="flex minicart__cnc-item-label-container">' + labelHtml +
      '<span class="minicart__cnc-status-label ' + statusClass + '">' + store.statusLabel + '</span>' +
      '</div></div>'
    );
  }

  function renderCncStoreShell(form) {
    var hasSelection = !!state.selectedCNC;
    // Mirrors the original's `:class="selectedCNC ? 'selectedcnc' : ''"` —
    // theme.css shrinks the container's padding/min-height once a store is
    // chosen (.minicart__click-collect-form-container.selectedcnc).
    form.classList.toggle('selectedcnc', hasSelection);

    var postcodeRow = form.querySelector('[data-cart-drawer-cnc-postcode-row]');
    if (postcodeRow) postcodeRow.hidden = hasSelection;

    var listWrap = form.querySelector('[data-cart-drawer-cnc-store-list-wrap]');
    var outletsEl = form.querySelector('[data-cart-drawer-cnc-outlets]');
    if (state.outlets.length > 1) {
      listWrap.hidden = false;
      outletsEl.innerHTML = hasSelection
        ? cncStoreRowHtml(state.selectedCNC, true)
        : state.outlets.map(function (store) { return cncStoreRowHtml(store, false); }).join('');
    } else {
      listWrap.hidden = true;
      outletsEl.innerHTML = '';
    }
    updateCheckoutState();
  }

  function refreshDeliveryAndCncChecks() {
    var jobs = [];
    if (state.deliveryMethod === 'home' || !state.deliveryStock) jobs.push(checkDelivery());
    if (state.selectedCNC) jobs.push(handleClickAndCollect());
    return Promise.all(jobs);
  }

  function updateCheckoutState() {
    var btn = panel.querySelector('[data-cart-drawer-checkout]');
    if (!btn) return;
    var disabled;
    if (state.deliveryMethod === 'store') disabled = !state.selectedCNC || state.selectedCNC.status !== 'available';
    else disabled = (state.deliveryStock ? state.deliveryStock.status !== 'available' : false) || state.outOfStockDelivery.length > 0;
    btn.disabled = disabled;
  }

  // ---------------------------------------------------------------------
  // Checkout — ported from submitCheckout() in assets/theme.js. Posts the
  // cnc-* cart attributes (read by checkout.liquid / the ReCharge subscription
  // footer via the normal cart object) then redirects to /checkout.
  // ---------------------------------------------------------------------
  function submitCheckout() {
    var btn = panel.querySelector('[data-cart-drawer-checkout]');
    if (btn) btn.disabled = true;
    var storeMode = state.deliveryMethod === 'store' && state.selectedCNC;
    var attributes = {
      'cnc-fulfilment-method': state.deliveryMethod,
      'cnc-outlet': storeMode ? state.selectedCNC.external_id : null,
      'cnc-store-name': storeMode ? state.selectedCNC.name : null,
      'cnc-store-address1': storeMode && state.selectedCNC.contact ? state.selectedCNC.contact.address1 : null,
      'cnc-store-suburb': storeMode && state.selectedCNC.contact ? state.selectedCNC.contact.suburb : null,
      'cnc-store-state': storeMode && state.selectedCNC.contact ? state.selectedCNC.contact.state : null,
      'cnc-store-postcode': storeMode && state.selectedCNC.contact ? state.selectedCNC.contact.postcode : null,
      'cnc-store-phone': storeMode && state.selectedCNC.contact ? state.selectedCNC.contact.phone : null,
      'cnc-store-email': storeMode && state.selectedCNC.contact ? state.selectedCNC.contact.email : null,
      'cnc-store-address2': storeMode && state.selectedCNC.contact ? state.selectedCNC.contact.address2 : null
    };
    cartFetch('/cart/update.js', { method: 'POST', body: JSON.stringify({ attributes: attributes }) })
      .then(function () { window.location.href = '/checkout'; })
      .catch(function (err) {
        console.error('cart-drawer: checkout attribute update failed', err);
        alert('Cart update error');
        if (btn) btn.disabled = false;
      });
  }

  // ---------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------
  initFreeShipping();
  syncWishlistIcons();
  updateTabChecks();
  // Ported from the original: the delivery stock/OOS check ran on every
  // `cart` change regardless of whether Click & Collect is enabled (it
  // drives the Delivery tab's own tick/cross and its OOS banners) — it
  // isn't gated behind settings.showClickAndCollect.
  checkDelivery();
})();